import { useDisclosure } from "@shared/hooks/useDisclosure";
import Spacer from "@ui-kit/layout/Spacer/Spacer.web";
import NavBarLocaleMenu from "@shared/components/application/navigation/NavBarLocaleMenu/NavBarLocaleMenu.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import ShowpassLogo from "@ui-kit/icons/ShowpassLogo/ShowpassLogo";
import { DASHBOARD_ROUTES } from "@app-contexts/dashboard/constants/dashboard-routes";
import { domElementIds } from "@shared/constants/common";
import { APP_ENV } from "@shared/env";
import useNavigationRoutes from "@shared/hooks/navigation/useNavigationRoutes";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { navigationLocales } from "@shared/modules/navigation/constants/navigation-translations";
import { useNavBarActions } from "@shared/modules/navigation/hooks/useNavBarActions";
import breakpoints from "@shared/theme/props/breakpoints";
import { isPlatform } from "@shared/utils/get-platform";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import url from "@shared/utils/urls/url";
import NavBarMenu from "@ui-application/navigation/NavBarMenu/NavBarMenu.web";
import UserMenu from "@ui-application/navigation/UserMenu/UserMenu.web";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import PageMaxWidth from "@ui-kit/layout/PageMaxWidth/PageMaxWidth.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Cookies from "js-cookie";
import { useContextualRouting } from "next-use-contextual-routing";
import Link from "@ui-kit/navigation/Link/Link.web";
import useRedirect from "@shared/hooks/useRedirect.web";
import { useEffect, useMemo } from "react";
import NavBarBanners from "../../data-display/NavBarBanners/NavBarBanners.web";
import NavBarCartDisplay from "../../data-display/NavBarCartDisplay/NavBarCartDisplay.web";
import NavBarMobileModal from "../NavBarMobileModal/NavBarMobileModal.web";
import { NavBarProps } from "./NavBar.props";
import WebviewComponentAdaptor from "../../other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";

const { sm } = breakpoints;
const { navbarId } = domElementIds;

type ContainerProps = React.ComponentProps<typeof Box> & {
  isDetailPage: boolean;
};

const Container: React.FC<ContainerProps> = ({
  isDetailPage,
  children,
  ...props
}) => (
  <Box
    width="100%"
    flexDirection="column"
    height={{
      base: "layout.navbarMobileHeight",
      sm: "layout.navbarDesktopHeight",
    }}
    alignItems="center"
    justifyContent="center"
    bg="bg"
    zIndex="sticky"
    gap="xs"
    top="none"
    display="flex"
    borderBottom="1px solid"
    borderColor="gray.400"
    position={isDetailPage ? undefined : "sticky"}
    boxShadow={isDetailPage ? "none" : undefined}
    {...props}
  >
    {children}
  </Box>
);

type ProfileImageProps = React.ComponentProps<typeof CDNImage>;

const ProfileImage: React.FC<ProfileImageProps> = (props) => (
  <Box
    as="span"
    display="inline-block"
    css={{
      "& img": {
        userSelect: "none",
        borderRadius: "full",
      },
    }}
  >
    <CDNImage {...props} />
  </Box>
);

const NavBar = ({ isDetailPage = false }: NavBarProps) => {
  const { redirect } = useRedirect();
  const { t } = useTranslation("common");
  const { makeContextualHref } = useContextualRouting();
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();
  const { loggedInLinks, goToLocale, handleLogout } = useNavBarActions();
  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < sm - 1,
    [windowWidth]
  );
  const { loggedOutLinks } = useNavigationRoutes();
  const {
    isOpen: isMobileMenuOpen,
    onOpen: mobileMenuOnOpen,
    onClose: mobileMenuOnClose,
  } = useDisclosure();

  const logInOnClick = () => {
    mobileMenuOnClose();

    redirect(makeContextualHref({ accountModal: "login" }), {
      as: "/accounts/login/",
      shallow: true,
    });
  };

  const signUpOnClick = () => {
    mobileMenuOnClose();

    redirect(makeContextualHref({ accountModal: "createAccount" }), {
      as: "/accounts/signup/",
      shallow: true,
    });
  };

  // Close the mobile modal on larger resolution if it is open.
  useEffect(() => {
    if (isMobileMenuOpen && !isSmallerThanSm) {
      mobileMenuOnClose();
    }
  }, [isSmallerThanSm, isMobileMenuOpen, mobileMenuOnClose]);

  const venueCookieExists =
    Boolean(Cookies.get("showpass-venue-id")) &&
    Cookies.get("showpass-venue-id") !== "None";

  return (
    <>
      <WebviewComponentAdaptor
        webComponent={
          <Container id={navbarId} isDetailPage={Boolean(isDetailPage)}>
            <Flex
              direction="column"
              gap={isSmallerThanSm ? "13px" : "18px"}
              width="100%"
            >
              <PageMaxWidth>
                <Flex alignItems="center">
                  <NavBarMobileModal
                    loggedInLinks={loggedInLinks}
                    loggedOutLinks={loggedOutLinks}
                    onClose={mobileMenuOnClose}
                    isOpen={isMobileMenuOpen}
                    locales={navigationLocales}
                    goToLocale={goToLocale}
                    handleLogout={handleLogout}
                    logInOnClick={logInOnClick}
                    signUpOnClick={signUpOnClick}
                  />
                  <Link href="/" asChild>
                    <Box
                      as="span"
                      data-testid="navbar-showpass-logo"
                      aria-label="showpass logo"
                      display="inline-block"
                      cursor="pointer"
                    >
                      <ShowpassLogo h="element.xl" w="auto" color="logo.fill" />
                    </Box>
                  </Link>
                  <Spacer />

                  {/* strictly checking for false to avoid flashing */}
                  {isLoggedIn || isLoggedIn === false ? (
                    <>
                      <Box display="block">
                        <NavBarCartDisplay
                          isDetailPage={Boolean(isDetailPage)}
                        />
                      </Box>
                      <Box
                        display={{ base: "none", sm: "flex" }}
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Box w="element.lg" />
                        <NavBarLocaleMenu />
                        <Box w="element.lg" />
                        {/** this button is for development/testing convenience; button links straight to the new manage dashboard */}
                        {(venueCookieExists || user?.impersonator_id) &&
                          (APP_ENV === "local" || APP_ENV === "preview") &&
                          !isDetailPage && (
                            <>
                              <Link
                                aria-label="manage"
                                href={DASHBOARD_ROUTES.ROOT}
                                asChild
                              >
                                <Button.Nav
                                  as="span"
                                  data-testid="navbar-manage"
                                >
                                  {t("Manage")}
                                </Button.Nav>
                              </Link>
                              <Box w="element.lg" />
                            </>
                          )}
                        {isLoggedIn && user ? (
                          <>
                            {(venueCookieExists || user.impersonator_id) && (
                              <>
                                <Link
                                  aria-label="dashboard"
                                  href={url("dashboard")}
                                  external
                                  target="_self"
                                  asChild
                                >
                                  <Button.Nav
                                    as="span"
                                    data-testid="navbar-dashboard"
                                  >
                                    {t("Dashboard")}
                                  </Button.Nav>
                                </Link>
                                <Box w="element.lg" />
                              </>
                            )}
                            <UserMenu
                              links={loggedInLinks}
                              handleLogout={handleLogout}
                              user={user}
                            />
                          </>
                        ) : (
                          <>
                            {!isDetailPage && (
                              <>
                                <NavBarMenu links={loggedOutLinks} />
                                <Box w="element.lg" />
                              </>
                            )}
                            {!isPlatform("desktop") && (
                              <>
                                <Button.Nav
                                  data-testid="navbar-create-account"
                                  onClick={signUpOnClick}
                                >
                                  {t("Create Account")}
                                </Button.Nav>
                                <Box minW="element.lg" />
                              </>
                            )}
                            <Button.Nav
                              data-testid="navbar-login"
                              variant="outline"
                              colorPalette="secondary"
                              onClick={logInOnClick}
                            >
                              {t("Log In")}
                            </Button.Nav>
                          </>
                        )}
                      </Box>
                      <Button.Nav
                        data-testid="navbar-mobile-user-menu"
                        data-sentry-mask
                        rightIcon={<Icon.ChevronDown boxSize="element.md" />}
                        display={{ base: "flex", sm: "none" }}
                        aria-label="Main menu"
                        onClick={mobileMenuOnOpen}
                      >
                        {isLoggedIn && user?.profile_picture ? (
                          <ProfileImage
                            width={27}
                            height={27}
                            alt={t("User profile image")}
                            src={
                              user.profile_picture ||
                              formatRelativeStaticPath("images/no-avatar.jpg")
                            }
                          />
                        ) : (
                          <Box
                            boxSize="element.xl"
                            borderRadius="full"
                            backgroundColor="gray.400"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Icon.User.Solid
                              boxSize="element.md"
                              color="light.100"
                            />
                          </Box>
                        )}
                      </Button.Nav>
                    </>
                  ) : null}
                </Flex>
              </PageMaxWidth>
            </Flex>
          </Container>
        }
        webviewComponent={null}
      />
      <NavBarBanners isDetailsPage={isDetailPage} />
    </>
  );
};

export default NavBar;
